<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="false"
      :loading="false"
      :hasDownload="true"
  >
    <template #subHeaderActions>
      <div class="d-flex align-items-center gap-2 gap-lg-3">
        <DateRangePicker id="stat-daterangepicker" @update:date="handleRangeDate" />
        <OutlineButton btn-class="btn-sm" icon-class="fas fa-download fs-3" type="primary" btn-text="button.download" @click="downloadView" :isLoading="isLoadingDownload"/>
      </div>
    </template>
    
    <template #layoutContent>
      <div class="row g-xl-10" id="viewToDownload">
        <div class="col-lg-12 col-xl-12 col-xxl-12 mb-5 mb-xl-0">
          <CrmInvoiceStatByStatus :load-stats="loadWidgetInvoiceStatData" :title="$t('invoices.invoice') " id="invoice" />

        </div>

        <div class="col-lg-12 col-xl-12 col-xxl-12 mb-5 mb-xl-0">
          <CrmSalesByPeriods :load-stats="loadWidgetTurnoverStatData" :title="$t('statistics.sale_turnover') " id="sale_turnover" />
        </div>

        <div class="col-lg-12 col-xl-12 col-xxl-12 mb-5 mb-xl-0">
          <CrmTopProduct :load-stats="loadWidgetSalesStatData" :title="$t('statistics.sale_products') " id="sale_products" />
        </div>

        <div class="col-lg-6 col-xl-6 col-xxl-6 mb-5 mb-xl-0">
          <CrmTopContacts :load-stats="loadWidgetSalesStatData" :title="$t('statistics.sale_contacts') " id="sale_contacts" />
        </div>

        <div class="col-lg-6 col-xl-6 col-xxl-6 mb-5 mb-xl-0">
          <CrmInvoiceTurnOver :load-stats="loadWidgetSalesStatData" :title="$t('statistics.sale_invoices') " id="sale_invoices" />
        </div>

        <div class="col-lg-12 col-xl-12 col-xxl-12 mb-5 mb-xl-0">
          <CrmTopCustomer :load-stats="loadWidgetInvoiceStatData" :title="$t('invoices.top_clients') " id="top_clients" />
        </div>

        <div class="col-lg-12 col-xl-12 col-xxl-12 mb-5 mb-xl-0">
          <CrmGetInvoiceAndAdvanceCountStatistic :load-stats="loadWidgetInvoiceStatData" :title="$t('invoices.invoice_moyen') " id="invoice_moyen" />
        </div>

        <div class="col-lg-12 col-xl-12 col-xxl-12 mb-5 mb-xl-0">
          <CrmGetAveragePaymentDelay :load-stats="loadWidgetInvoiceStatData" :title="$t('invoices.invoice_moyen_due_date') " id="invoice_moyen_due_date" />
        </div>
      </div>
    </template>
  </SubLayout>
</template>
<script setup>
import {onMounted, ref, nextTick, watch, computed, shallowRef} from "vue";
import {useStore} from "vuex";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import i18n from '@/plugins/i18n';
import {$formatLocalAmount} from "@/mixins/Helpers";
import DateRangePicker from "@/components/DateRangePicker.vue";
import { useLocales } from "@/composables/locales";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import OutlineButton from "@/components/OutlineButton";
import CrmInvoiceStatByStatus from "@/components/stats/widgets/CrmInvoiceStatByStatus.vue";
import CrmSalesByPeriods from "@/components/stats/widgets/CrmSalesByPeriods.vue";
import CrmTopProduct from "@/components/stats/widgets/CrmTopProduct.vue";
import CrmTopContacts from "@/components/stats/widgets/CrmTopContacts.vue";
import CrmInvoiceTurnOver from "@/components/stats/widgets/CrmInvoiceTurnOver.vue";
import CrmTopCustomer from "@/components/stats/widgets/CrmTopCustomer.vue";
import CrmGetInvoiceAndAdvanceCountStatistic from "@/components/stats/widgets/CrmGetInvoiceAndAdvanceCountStatistic.vue";
import CrmGetAveragePaymentDelay from "@/components/stats/widgets/CrmGetAveragePaymentDelay.vue";

const {locale} = useLocales()
const store = useStore()

const title = ref( "navigation.statistics")
const breadcrumbs = ref([
  {
    name: 'navigation.statistics',
    path: '/admin'
  }
])

const query = ref({
  start_date: '',
  end_date: '',
  payment_type_id: '', 
  contact_id: ''
})



const averageInvoices = ref([])
const saleEstimates = ref([])
const averagePaidInvoiceAmount = ref(0)

const isLoadingDownload = ref(false)

const contacts = computed(() => store.getters['contacts/getContacts'])
const defaultCurrency = computed(() => store.getters['company/getCompanyCurrency'])

watch(() => query.value.contact_id, (value) => {
  loadData()
})

watch(() => query.value.payment_type_id, (value) => {
  loadData()
})

function handleRangeDate (range) {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
  if(query.value.start_date && query.value.end_date) loadData()
}




function downloadView(){
  isLoadingDownload.value = true
  store.dispatch('statistics/getDownloadInvoiceStatistics', query.value)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', i18n.global.t('invoices.invoice')+query.value.start_date+'.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.toastr['success'](i18n.global.t('notification.download_invoice'))
      })
      .catch(err => {
      }).finally(() => isLoadingDownload.value = false)
}

async function loadWidgetInvoiceStatData(params) {
  try {
    const res = await store.dispatch('invoices/getInvoiceStats', params)
    return res
  } finally {
  }
}   
  
async function loadWidgetTurnoverStatData(params) {
  try {
    const res = await store.dispatch('stats/loadSaleStatistics', params)
    return res
  } finally {
  }
}

async function loadWidgetSalesStatData(params) {
  try {
    const res = await store.dispatch('stats/loadStatistics', params)
    return res
  } finally {
  }
}

function loadData(){
  Promise.all([
    store.dispatch('company/generalInformation'),
    store.dispatch('invoices/getInvoiceStats', {startDate: query.value.start_date, endDate: query.value.end_date, payment_type_id: query.value.payment_type_id, contact_id: query.value.contact_id}).then((res) => {
      averageInvoices.value = res.data?.invoiceStatusTurnover
      averagePaidInvoiceAmount.value = res.data?.averagePaidInvoiceAmount
      saleEstimates.value = res.data?.estimate_sales_by_period
    }),
    store.dispatch('invoices/loadInvoiceStatus'),
    store.dispatch('contacts/loadContacts'),
    store.dispatch('payments/loadPaymentTypes')
  ]).then(() => {
  })
}

onMounted(() => {
  let start = moment().subtract(29, 'days');
  let end = moment();
  query.value.start_date = start.format('DD-MM-YYYY')
  query.value.end_date = end.format('DD-MM-YYYY')

  if(query.value.start_date && query.value.end_date){
    nextTick(() => {
      loadData();
    });
  }
})








</script>
